import { FC, useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../auth'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { toast } from 'react-toastify'
import { getCompanyGroupAdmins, getInventoryItemHeads, getVesselsByCompany } from '../core/_requests'
import { CompanyGroupAdmin, InventoryItemHead, Vessel } from '../core/_models'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface SubHeadModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (payload: { name: string; inventoryItemHeadId: number }) => Promise<void>
  vesselId?: number
  embedded?: boolean
  stayOpenOnSubmit?: boolean
}

const SubHeadModal: FC<SubHeadModalProps> = ({
  visible,
  onClose,
  onSubmit,
  vesselId,
  embedded = false,
  stayOpenOnSubmit = false,
}) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_SUBHEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBHEAD_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'

  const [name, setName] = useState('')
  const [inventoryItemHeadId, setInventoryItemHeadId] = useState<number | ''>('')
  const [heads, setHeads] = useState<InventoryItemHead[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingHeads, setIsLoadingHeads] = useState(false)
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  const effectiveVesselId =
    (libraryScopeAll || libraryScopeCompany) && selectedVesselId ? Number(selectedVesselId) : vesselId

  const effectiveCgaId = libraryScopeAll && selectedCompanyId ? Number(selectedCompanyId) : undefined

  const loadCompanies = useCallback(async () => {
    setIsLoadingDropdowns(true)
    try {
      const data = await getCompanyGroupAdmins()
      setCompanies(data)
    } catch (error) {
      console.error('Error loading companies:', error)
      toast.error('Failed to load companies')
      setCompanies([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }, [])

  const loadVessels = useCallback(async (companyUsername: string) => {
    setIsLoadingDropdowns(true)
    try {
      const data = await getVesselsByCompany(companyUsername)
      setVessels(data || [])
    } catch (error) {
      console.error('Error loading vessels:', error)
      toast.error('Failed to load vessels')
      setVessels([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }, [])

  const loadVesselsForCurrentUser = useCallback(async () => {
    const companyUsername =
      (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
    if (!companyUsername) return
    await loadVessels(companyUsername)
  }, [currentUser, loadVessels])

  const loadHeads = useCallback(async (resolvedVesselId?: number, scopedCgaId?: number) => {
    if (libraryScopeAll && !resolvedVesselId && !scopedCgaId) {
      setHeads([])
      return
    }

    setIsLoadingHeads(true)
    try {
      const data = await getInventoryItemHeads(resolvedVesselId, scopedCgaId)
      setHeads(data)
    } catch (error) {
      console.error('Error loading heads:', error)
      toast.error('Failed to load heads')
      setHeads([])
    } finally {
      setIsLoadingHeads(false)
    }
  }, [libraryScopeAll])

  useEffect(() => {
    if (!visible) return

    if (libraryScopeAll) {
      setSelectedCompanyId('')
      setSelectedVesselId(vesselId ?? '')
    } else if (libraryScopeCompany) {
      setSelectedCompanyId('')
      setSelectedVesselId(vesselId ?? '')
    } else {
      setSelectedCompanyId('')
      setSelectedVesselId('')
    }
  }, [visible, libraryScopeAll, libraryScopeCompany, vesselId])

  useEffect(() => {
    if (!visible) return

    if (libraryScopeAll) {
      loadCompanies()
    } else if (libraryScopeCompany) {
      loadVesselsForCurrentUser()
    }
  }, [visible, libraryScopeAll, libraryScopeCompany, loadCompanies, loadVesselsForCurrentUser])

  useEffect(() => {
    if (!visible || !libraryScopeAll) return

    if (!selectedCompanyId) {
      setVessels([])
      setSelectedVesselId('')
      return
    }

    const selectedCompany = companies.find((c) => c.id === Number(selectedCompanyId))
    if (selectedCompany?.username) {
      loadVessels(selectedCompany.username)
    }
  }, [visible, libraryScopeAll, selectedCompanyId, companies, loadVessels])

  useEffect(() => {
    if (!visible) return
    loadHeads(effectiveVesselId ?? undefined, effectiveCgaId)
  }, [visible, effectiveVesselId, effectiveCgaId, loadHeads])

  const resetState = () => {
    setName('')
    setInventoryItemHeadId('')
    setHeads([])
    setCompanies([])
    setVessels([])
    setSelectedCompanyId('')
    setSelectedVesselId('')
    setIsLoading(false)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast.error('Please enter a sub head name')
      return
    }

    if (!inventoryItemHeadId) {
      toast.error('Please select a head')
      return
    }

    setIsLoading(true)
    try {
      await onSubmit({
        name: name.trim(),
        inventoryItemHeadId: Number(inventoryItemHeadId)
      })
      toast.success('Sub head created successfully!')
      if (stayOpenOnSubmit) {
        resetState()
      } else {
        handleClose()
      }
    } catch (error: any) {
      console.error('Error creating sub head:', error)
      toast.error('Failed to create sub head')
    } finally {
      setIsLoading(false)
    }
  }

  if (!visible) return null

  const modalContent = (
    <div
      className='modal-content bg-white'
      style={{
        height: embedded ? '100%' : undefined,
        borderRadius: embedded ? '12px' : undefined,
        boxShadow: embedded ? '0 12px 40px rgba(15,23,42,0.06)' : undefined,
        border: embedded ? '1px solid #E9EEF5' : undefined,
      }}
    >
      <div className='modal-header border-bottom'>
        <h3 className='modal-title fw-bold text-dark'>Add New Sub Head</h3>
        <button type='button' className='btn-close' onClick={handleClose}></button>
      </div>

      <form onSubmit={handleSubmit} style={{height: embedded ? 'calc(100% - 60px)' : undefined, display: 'flex', flexDirection: 'column'}}>
        <div className='modal-body' style={{overflowY: embedded ? 'auto' : undefined, flex: embedded ? '1 1 auto' : undefined}}>
          {libraryScopeAll && (
            <div className='row g-3 mb-4'>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7 required'>Company</label>
                <SearchableSelect
                  className='form-select'
                  value={selectedCompanyId}
                  onChange={(e) => {
                    const nextCompanyId = e.target.value ? Number(e.target.value) : ''
                    setSelectedCompanyId(nextCompanyId)
                    setSelectedVesselId('')
                    setHeads([])
                    setInventoryItemHeadId('')
                  }}
                  disabled={isLoadingDropdowns}
                  required
                >
                  <option value=''>Select a company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7'>Vessel (optional)</label>
                <SearchableSelect
                  className='form-select'
                  value={selectedVesselId}
                  onChange={(e) => {
                    const nextVesselId = e.target.value ? Number(e.target.value) : ''
                    setSelectedVesselId(nextVesselId)
                    setHeads([])
                    setInventoryItemHeadId('')
                  }}
                  disabled={isLoadingDropdowns || !selectedCompanyId}
                >
                  <option value=''>Company level (no vessel)</option>
                  {vessels.map((vessel) => (
                    <option key={vessel.id} value={vessel.id}>
                      {vessel.fleet_name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            </div>
          )}

          {libraryScopeCompany && (
            <div className='mb-4'>
              <label className='form-label fw-semibold text-muted fs-7'>Vessel (optional)</label>
              <SearchableSelect
                className='form-select'
                value={selectedVesselId}
                onChange={(e) => {
                  const nextVesselId = e.target.value ? Number(e.target.value) : ''
                  setSelectedVesselId(nextVesselId)
                  setHeads([])
                  setInventoryItemHeadId('')
                }}
                disabled={isLoadingDropdowns}
              >
                <option value=''>{isLoadingDropdowns ? 'Loading vessels...' : 'Company level (no vessel)'}</option>
                {vessels.map((vessel) => (
                  <option key={vessel.id} value={vessel.id}>
                    {vessel.fleet_name}
                  </option>
                ))}
              </SearchableSelect>
            </div>
          )}

          <div className='mb-4'>
            <label className='form-label fw-semibold text-muted fs-7 required'>Sub Head Name</label>
            <input
              type='text'
              className='form-control'
              placeholder='Enter sub head name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className='mb-4'>
            <label className='form-label fw-semibold text-muted fs-7 required'>Head</label>
            <SearchableSelect
              className='form-select'
              value={inventoryItemHeadId}
              onChange={(e) => setInventoryItemHeadId(Number(e.target.value))}
              required
              disabled={isLoadingHeads || (libraryScopeAll && !selectedCompanyId)}
            >
              <option value=''>
                {isLoadingHeads
                  ? 'Loading heads...'
                  : libraryScopeAll && !selectedCompanyId
                  ? 'Select company first'
                  : 'Select a head'}
              </option>
              {heads.map((head) => (
                <option key={head.id} value={head.id}>
                  {head.name}
                </option>
              ))}
            </SearchableSelect>
          </div>
        </div>

        <div className='modal-footer border-top' style={{flexShrink: 0}}>
          <button
            type='button'
            className='btn btn-light'
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn_primary'
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Sub Head'}
          </button>
        </div>
      </form>
    </div>
  )

  if (embedded) {
    return (
      <div
        className='h-100 d-flex flex-column'
        style={{background: '#F8FAFC', padding: '12px', overflow: 'hidden'}}
      >
        {modalContent}
      </div>
    )
  }

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-dialog-centered' role='document'>
        {modalContent}
      </div>
    </div>
  )
}

export default SubHeadModal
